"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { Search, Package, BookOpen, Leaf } from "lucide-react";
import productsData from "@/lib/content/products.json";
import ingredientsData from "@/lib/content/ingredients.json";
import blogData from "@/lib/content/blog-content.json";
import { isRealPhoto } from "@/lib/brand-mapping";

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  category: "Products" | "Blog" | "Ingredients";
  image?: string;
}

interface SearchOverlayProps {
  locale: string;
  open: boolean;
  onClose: () => void;
  ui?: {
    typeToBegin?: string;
    noResults?: string;
    escClose?: string;
    placeholder?: string;
    subtitle?: string;
    hints?: string;
    nothingFor?: string;
    trySuggestion?: string;
    browse?: string;
    results?: string;
    navigate?: string;
    openLabel?: string;
  };
}

// Build search index from static data
function buildIndex(locale: string): SearchResult[] {
  const results: SearchResult[] = [];

  // Products — use the catalog's real image only when it's a current shoot photo.
  // Old vector silhouettes get filtered out so Next/Image doesn't 400 on missing jpgs.
  for (const product of productsData.products) {
    const img = (product as { image?: string }).image;
    results.push({
      id: `product-${product.id}`,
      title: product.name,
      subtitle: product.description.slice(0, 100) + "…",
      href: `/${locale}/products/${product.category}/${product.sku}`,
      category: "Products",
      image: img && isRealPhoto(img) ? img : undefined,
    });
  }

  // Blog articles
  const localeKey = locale as keyof typeof blogData;
  const blogLocale = (blogData as Record<string, Record<string, { title: string; excerpt: string }>>)[localeKey] ?? blogData["en"];
  for (const [slug, article] of Object.entries(blogLocale)) {
    results.push({
      id: `blog-${slug}`,
      title: article.title,
      subtitle: article.excerpt.slice(0, 100) + "…",
      href: `/${locale}/blog/${slug}`,
      category: "Blog",
    });
  }

  // Ingredients
  for (const ingredient of ingredientsData.ingredients) {
    results.push({
      id: `ingredient-${ingredient.slug}`,
      title: ingredient.inci_name,
      subtitle: ingredient.benefits,
      href: `/${locale}/ingredients/${ingredient.slug}`,
      category: "Ingredients",
    });
  }

  return results;
}

function scoreResult(result: SearchResult, query: string): number {
  const q = query.toLowerCase();
  const title = result.title.toLowerCase();
  const subtitle = result.subtitle.toLowerCase();
  if (title === q) return 100;
  if (title.startsWith(q)) return 80;
  if (title.includes(q)) return 60;
  if (subtitle.includes(q)) return 30;
  return 0;
}

const CATEGORY_ORDER: SearchResult["category"][] = ["Products", "Blog", "Ingredients"];
const CATEGORY_ICONS = {
  Products: Package,
  Blog: BookOpen,
  Ingredients: Leaf,
};

export default function SearchOverlay({ locale, open, onClose, ui }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const indexRef = useRef<SearchResult[] | null>(null);

  // Build index lazily once
  if (!indexRef.current) {
    indexRef.current = buildIndex(locale);
  }

  const search = useCallback((q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const index = indexRef.current!;
    const scored = index
      .map((r) => ({ result: r, score: scoreResult(r, q) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((x) => x.result);
    setResults(scored);
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery("");
      setResults([]);
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    search(query);
  }, [query, search]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter" && results.length > 0) {
        const target = results[activeIndex];
        if (target) {
          window.location.href = target.href;
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, results, activeIndex, onClose]);

  if (!open) return null;

  const grouped = CATEGORY_ORDER.reduce<Record<string, SearchResult[]>>((acc, cat) => {
    const items = results.filter((r) => r.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const flatResults = CATEGORY_ORDER.flatMap((cat) => grouped[cat] ?? []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "10vh",
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "oklch(0.10 0 0 / 0.45)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      />

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 720,
          background: "var(--bp-bg)",
          border: "1px solid var(--bp-ink)",
          overflow: "hidden",
          boxShadow: "0 24px 80px oklch(0.10 0 0 / 0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "8px 14px",
            background: "var(--bp-ink)",
            color: "var(--bp-bg)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span className="bp-mono-sm" style={{ color: "var(--bp-bg)", letterSpacing: "0.18em" }}>
            SEARCH · BPDERMA.COM
          </span>
          <button
            onClick={onClose}
            className="bp-mono-sm"
            style={{
              color: "var(--bp-bg)",
              background: "none",
              border: 0,
              cursor: "pointer",
            }}
            aria-label="Close search"
          >
            ESC ×
          </button>
        </div>

        {/* Input row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "18px 22px",
            borderBottom: "1px solid var(--bp-rule)",
          }}
        >
          <Search className="w-5 h-5" style={{ color: "var(--bp-ink-3)", flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={ui?.placeholder ?? "Search products, articles, ingredients…"}
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1,
              background: "transparent",
              border: 0,
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              fontSize: 24,
              letterSpacing: "-0.01em",
              color: "var(--bp-ink)",
              outline: "none",
            }}
          />
        </div>

        {/* Results body */}
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {query.trim() === "" && (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: "var(--bp-ink-3)",
              }}
            >
              <span className="bp-mono-sm">{ui?.typeToBegin ?? "TYPE TO BEGIN"}</span>
              <p
                style={{
                  marginTop: 16,
                  fontFamily: "var(--bp-serif)",
                  fontSize: 22,
                  fontWeight: 300,
                  letterSpacing: "-0.01em",
                  color: "var(--bp-ink-2)",
                }}
              >
                {ui?.subtitle ?? "Search products, journal articles, and ingredients."}
              </p>
              <div className="bp-mono-sm" style={{ marginTop: 12 }}>
                {ui?.hints ?? "ESC TO CLOSE · ↑↓ TO NAVIGATE · ↵ TO OPEN"}
              </div>
            </div>
          )}

          {query.trim() !== "" && results.length === 0 && (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: "var(--bp-ink-3)",
              }}
            >
              <span className="bp-mono-sm">{ui?.noResults ?? "NO RESULTS"}</span>
              <p
                style={{
                  marginTop: 16,
                  fontFamily: "var(--bp-serif)",
                  fontSize: 22,
                  fontWeight: 300,
                  color: "var(--bp-ink)",
                }}
              >
                {ui?.nothingFor ?? "Nothing for"} &ldquo;{query}&rdquo;.
              </p>
              <p style={{ marginTop: 8, fontSize: 13, color: "var(--bp-ink-3)" }}>
                {ui?.trySuggestion ?? "Try: niacinamide · cleanser · retinol · EU regulation"}
              </p>
              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  justifyContent: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {["products", "ingredients", "blog"].map((s) => (
                  <Link
                    key={s}
                    href={`/${locale}/${s}`}
                    onClick={onClose}
                    className="bp-mono-sm"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid var(--bp-ink)",
                      color: "var(--bp-ink)",
                    }}
                  >
                    {ui?.browse ?? "BROWSE"} {s.toUpperCase()}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.length > 0 &&
            CATEGORY_ORDER.map((cat) => {
              const items = grouped[cat];
              if (!items) return null;
              const Icon = CATEGORY_ICONS[cat];
              return (
                <div key={cat}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "16px 22px 4px",
                      borderTop: "1px solid var(--bp-rule)",
                      background: "var(--bp-bg-2)",
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: "var(--bp-ink-3)" }} />
                    <span className="bp-mono-sm">{cat.toUpperCase()}</span>
                  </div>
                  {items.map((result) => {
                    const flatIdx = flatResults.indexOf(result);
                    const isActive = flatIdx === activeIndex;
                    return (
                      <Link
                        key={result.id}
                        href={result.href}
                        onClick={onClose}
                        onMouseEnter={() => setActiveIndex(flatIdx)}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "44px 1fr auto",
                          gap: 14,
                          alignItems: "center",
                          padding: "14px 22px",
                          background: isActive ? "var(--bp-bg-2)" : "var(--bp-bg)",
                          color: "var(--bp-ink)",
                          textDecoration: "none",
                          borderLeft: `3px solid ${isActive ? "var(--bp-ink)" : "transparent"}`,
                        }}
                      >
                        {result.category === "Products" && result.image ? (
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              background: "var(--bp-bg-2)",
                              border: "1px solid var(--bp-rule)",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src={result.image}
                              alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              background: "var(--bp-bg-2)",
                              border: "1px solid var(--bp-rule)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {result.category === "Blog" && (
                              <BookOpen className="w-4 h-4" style={{ color: "var(--bp-ink-3)" }} />
                            )}
                            {result.category === "Ingredients" && (
                              <Leaf className="w-4 h-4" style={{ color: "var(--bp-ink-3)" }} />
                            )}
                            {result.category === "Products" && (
                              <Package className="w-4 h-4" style={{ color: "var(--bp-ink-3)" }} />
                            )}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontFamily: "var(--bp-serif)",
                              fontSize: 17,
                              fontWeight: 300,
                              letterSpacing: "-0.01em",
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {result.title}
                          </div>
                          <div
                            className="bp-mono-sm"
                            style={{
                              marginTop: 4,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {result.subtitle}
                          </div>
                        </div>
                        <span
                          className="bp-mono-sm"
                          style={{
                            color: isActive ? "var(--bp-ink)" : "var(--bp-ink-3)",
                            flexShrink: 0,
                          }}
                        >
                          {isActive ? "↵" : "→"}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              );
            })}
        </div>

        {results.length > 0 && (
          <div
            className="bp-mono-sm"
            style={{
              padding: "10px 22px",
              borderTop: "1px solid var(--bp-rule)",
              background: "var(--bp-bg-2)",
              display: "flex",
              gap: 16,
              alignItems: "center",
            }}
          >
            <span>{String(results.length).padStart(2, "0")} {ui?.results ?? "RESULTS"}</span>
            <span>{ui?.navigate ?? "↑↓ NAVIGATE"}</span>
            <span>{ui?.openLabel ?? "↵ OPEN"}</span>
            <span>{ui?.escClose ?? "ESC CLOSE"}</span>
          </div>
        )}
      </div>
    </div>
  );
}
